/* Eva Everyday — progressively enhanced Shopify storefront. */
(() => {
  'use strict';
  document.documentElement.classList.replace('no-js', 'js');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 900px)');
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const shopRoot = () => window.Shopify?.routes?.root || '/';

  function syncQuantity(control) {
    const input = $('input', control);
    if (!input) return;
    const min = Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;
    const value = Number(input.value);
    const down = $('[data-quantity-step="-1"]', control);
    const up = $('[data-quantity-step="1"]', control);
    if (down) down.disabled = value <= min;
    if (up) up.disabled = value >= max;
  }

  function selectMedia(gallery, id) {
    const slides = $$('[data-media-id]', gallery);
    if (!slides.some(slide => slide.dataset.mediaId === String(id))) return;
    slides.forEach(slide => {
      slide.hidden = slide.dataset.mediaId !== String(id);
      if (slide.hidden) $$('video', slide).forEach(video => video.pause());
    });
    $$('[data-media-target]', gallery).forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.mediaTarget === String(id)));
    });
  }

  function initProduct(product) {
    if (product.dataset.initialized) return;
    product.dataset.initialized = 'true';
    const form = $('.product-form', product);
    const variantSelect = $('[data-variant-select]', product);
    const planSelect = $('[data-plan-select]', product);
    const quantity = $('input[name="quantity"]', product);
    const button = $('button[name="add"]', form);
    const buttonText = $('[data-add-text]', product);
    const gallery = $('[data-gallery]', product);
    const message = $('[data-product-message]', product);
    const bagLink = $('[data-view-bag]', product);
    let adding = false;

    const updatePrice = () => {
      const variant = variantSelect.selectedOptions[0];
      const allocation = planSelect?.selectedOptions[0];
      const source = allocation?.value ? allocation : variant;
      $('[data-price]', product).textContent = source.dataset.price || variant.dataset.price;
      const compare = $('[data-compare]', product);
      compare.textContent = source.dataset.compare || '';
      compare.hidden = !source.dataset.compare;
      $('[data-unit-price]', product).textContent = allocation?.value ? '' : variant.dataset.unit || '';
      const available = variant.dataset.available === 'true';
      const planMissing = variant.dataset.requiresPlan === 'true' && !planSelect?.value;
      button.disabled = adding || !available || planMissing;
      buttonText.textContent = adding ? product.dataset.addingLabel : available ? product.dataset.addLabel : product.dataset.soldLabel;
    };

    const updateVariant = (updateURL = false) => {
      const variant = variantSelect.selectedOptions[0];
      if (!variant) return;
      if (planSelect) {
        const previousPlan = planSelect.value;
        const options = $$('template[data-plans-for]', product).find(t => t.dataset.plansFor === variant.value);
        if (options) planSelect.replaceChildren(options.content.cloneNode(true));
        if ([...planSelect.options].some(option => option.value === previousPlan)) planSelect.value = previousPlan;
        const hasPlans = [...planSelect.options].some(option => option.value !== '');
        $('[data-plan-wrap]', product).hidden = !hasPlans;
        planSelect.disabled = !hasPlans;
      }
      quantity.min = variant.dataset.min || '1';
      quantity.step = variant.dataset.step || '1';
      if (variant.dataset.max) quantity.max = variant.dataset.max;
      else quantity.removeAttribute('max');
      const min = Number(quantity.min);
      const step = Number(quantity.step);
      const max = quantity.max ? Number(quantity.max) : Infinity;
      quantity.value = String(Math.min(max, min + Math.max(0, Math.floor((Number(quantity.value) - min) / step)) * step));
      syncQuantity(quantity.closest('.quantity-control'));
      if (gallery && variant.dataset.media) selectMedia(gallery, variant.dataset.media);
      message.textContent = '';
      bagLink.hidden = true;
      updatePrice();
      if (updateURL && !window.Shopify?.designMode) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.value);
        url.searchParams.delete('selling_plan');
        if (planSelect?.value) url.searchParams.set('selling_plan', planSelect.value);
        window.history.replaceState({}, '', url);
      }
    };

    variantSelect.addEventListener('change', () => updateVariant(true));
    planSelect?.addEventListener('change', () => {
      updatePrice();
      if (!window.Shopify?.designMode) {
        const url = new URL(window.location.href);
        if (planSelect.value) url.searchParams.set('selling_plan', planSelect.value);
        else url.searchParams.delete('selling_plan');
        window.history.replaceState({}, '', url);
      }
    });
    updateVariant();
    const requestedPlan = new URL(window.location.href).searchParams.get('selling_plan');
    if (requestedPlan && planSelect && [...planSelect.options].some(option => option.value === requestedPlan)) {
      planSelect.value = requestedPlan;
      updatePrice();
    }

    form.addEventListener('submit', async event => {
      if (!window.fetch) return; // The native Shopify product form remains the fallback.
      event.preventDefault();
      if (adding || button.disabled || !form.reportValidity()) return;
      const payload = new FormData(form);
      adding = true;
      updatePrice();
      button.setAttribute('aria-busy', 'true');
      message.textContent = '';
      bagLink.hidden = true;
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      let serverMessage = '';
      try {
        const response = await fetch(shopRoot() + 'cart/add.js', {
          method: 'POST', body: payload, headers: { Accept: 'application/json' }, signal: controller.signal
        });
        const result = await response.json();
        if (!response.ok) {
          serverMessage = typeof result.description === 'string' ? result.description : product.dataset.errorLabel;
          throw new Error(serverMessage);
        }
        window.clearTimeout(timeout);
        message.textContent = product.dataset.addedLabel;
        message.classList.remove('form-error');
        bagLink.hidden = false;
        // A failed counter refresh must never turn a successful add into a retry prompt.
        try {
          const cartResponse = await fetch(shopRoot() + 'cart.js', { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(5000) });
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            $$('[data-cart-count]').forEach(el => { el.textContent = String(cart.item_count); });
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
          }
        } catch { /* The bag itself always displays the server's current cart. */ }
      } catch (error) {
        message.textContent = serverMessage || 'Connection interrupted. Check your bag before trying again.';
        message.classList.add('form-error');
        bagLink.hidden = false;
      } finally {
        window.clearTimeout(timeout);
        adding = false;
        button.removeAttribute('aria-busy');
        updatePrice();
      }
    });
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      if (!reduced.matches && document.body.dataset.motion === 'true') entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.12 }) : null;

  let scheduled = false;
  let frames = [];
  function paintParallax() {
    scheduled = false;
    const enabled = !reduced.matches && desktop.matches && document.body.dataset.parallax === 'true';
    frames.forEach(frame => {
      const img = $('.parallax-image', frame);
      if (!img) return;
      if (!enabled) { img.style.removeProperty('--parallax-y'); return; }
      const rect = frame.getBoundingClientRect();
      if (rect.bottom < -80 || rect.top > window.innerHeight + 80) return;
      const offset = Math.max(-20, Math.min(20, (rect.top + rect.height / 2 - window.innerHeight / 2) * 0.045));
      img.style.setProperty('--parallax-y', `${offset.toFixed(1)}px`);
    });
  }
  function requestPaint() {
    if (!scheduled) { scheduled = true; window.requestAnimationFrame(paintParallax); }
  }
  function init(root = document) {
    $$('[data-gallery]', root).forEach(gallery => {
      const initial = $('[data-initial-media]', gallery) || $('[data-media-id]', gallery);
      if (initial) selectMedia(gallery, initial.dataset.mediaId);
      gallery.classList.add('gallery-enhanced');
    });
    $$('[data-product]', root).forEach(initProduct);
    $$('.quantity-control', root).forEach(syncQuantity);
    $$('[data-reveal]', root).forEach(el => observer?.observe(el));
    frames = $$('[data-parallax-frame]');
    requestPaint();
  }

  document.addEventListener('click', event => {
    const stepButton = event.target.closest('[data-quantity-step]');
    if (stepButton) {
      const control = stepButton.closest('.quantity-control');
      const input = $('input', control);
      if (!input.value) input.value = input.min || '1';
      try { Number(stepButton.dataset.quantityStep) > 0 ? input.stepUp() : input.stepDown(); } catch { return; }
      input.dispatchEvent(new Event('change', { bubbles: true }));
      syncQuantity(control);
    }
    const thumb = event.target.closest('[data-media-target]');
    if (thumb) selectMedia(thumb.closest('[data-gallery]'), thumb.dataset.mediaTarget);
    $$('[data-menu][open]').forEach(menu => {
      if (!menu.contains(event.target) || event.target.closest('a')) menu.open = false;
    });
    $$('.nav-dropdown[open]').forEach(menu => {
      if (!menu.contains(event.target)) menu.open = false;
    });
  });
  // Save entered codes through Shopify, then retain the native checkout POST.
  // Checkout performs the final eligibility checks, including shipping/customer rules.
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-discount-checkout]');
    if (!form || form.dataset.discountReady === 'true') return;
    const field = $('[data-checkout-code]', form);
    const codes = [...new Set(field.value.split(',').map(code => code.trim()).filter(Boolean))].join(',');
    if (!codes || !window.fetch) return;
    event.preventDefault();
    if (form.dataset.discountPending === 'true') return;
    const button = $('button[name="checkout"]', form);
    const error = $('[data-discount-error-message]', form);
    form.dataset.discountPending = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    error.hidden = true;
    field.removeAttribute('aria-invalid');
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(shopRoot() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes, note: $('textarea[name="note"]', form)?.value || '' }),
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Discount request failed');
      await response.json();
      form.dataset.discountReady = 'true';
      button.disabled = false;
      form.requestSubmit(button);
    } catch {
      error.textContent = form.dataset.discountError;
      error.hidden = false;
      field.setAttribute('aria-invalid', 'true');
      field.focus();
    } finally {
      window.clearTimeout(timer);
      form.dataset.discountPending = 'false';
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });
  document.addEventListener('input', event => {
    const form = event.target.closest('[data-discount-checkout]');
    if (form) delete form.dataset.discountReady;
  });
  document.addEventListener('change', event => {
    if (event.target.closest('.quantity-control')) syncQuantity(event.target.closest('.quantity-control'));
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const open = $$('details[open]').filter(el => el.matches('[data-menu], .nav-dropdown'));
    open.reverse().forEach(el => { el.open = false; $('summary', el)?.focus(); });
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    $$('[data-reveal]', event.target).forEach(el => observer?.unobserve(el));
    frames = frames.filter(frame => !event.target.contains(frame));
  });
  document.addEventListener('shopify:block:select', event => {
    const details = event.target.closest('details');
    if (details) details.open = true;
  });
  window.addEventListener('scroll', requestPaint, { passive: true });
  window.addEventListener('resize', requestPaint, { passive: true });
  reduced.addEventListener('change', requestPaint);
  desktop.addEventListener('change', () => { if (desktop.matches) $$('[data-menu]').forEach(menu => { menu.open = false; }); requestPaint(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
